import { notify } from '@/lib/toast';
import { useState, useMemo, useCallback } from 'react';
import { RecordLink } from '@/components/records/RecordLink';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Plus, Trash2, Send, Receipt, XCircle, Search,
  ShoppingCart, Banknote, CreditCard, Smartphone, Building2, Scissors,
  UserCheck, ArrowRightLeft, Gift, Combine, Truck, Phone,
  Salad, Utensils, Cookie, StickyNote, Tag,
} from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { Combobox } from '@/components/ui/combobox';
import { useAuthStore } from '@/stores/auth.store';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { BankAccountField } from '@/components/pos/BankAccountField';
import { LineDiscountEditor, type LineDiscountValue } from '@/components/pos/LineDiscountEditor';
import {
  diningItemsDiscount,
  diningItemsNet,
  diningLineDiscount,
  diningLineGross,
} from '@/lib/dining-order-total';
import { effectiveBankAccountId, rememberBankAccountId, type BankAccountOption } from '@/lib/bank-accounts';
import toast from 'react-hot-toast';
import type { DiningOrder } from '@/types/restaurant';

const STATUS_COLORS: Record<string, string> = {
  OPEN:      'bg-success/10 text-success-text',
  PREPARING: 'bg-warning/10 text-warning-text',
  READY:     'bg-success/10 text-success-text',
  SERVED:    'bg-info/10 text-info-text',
  BILLED:    'bg-muted text-muted-foreground',
  VOIDED:    'bg-destructive/10 text-destructive-text',
};

const KITCHEN_STATUS_COLORS: Record<string, string> = {
  PENDING:   'bg-muted text-muted-foreground',
  PREPARING: 'bg-warning/10 text-warning-text',
  READY:     'bg-success/10 text-success-text',
  SERVED:    'bg-info/10 text-info-text',
  CANCELLED: 'bg-destructive/10 text-destructive-text',
};

const COURSE_META: Record<string, { labelKey: string; Icon: typeof Salad }> = {
  STARTER: { labelKey: 'courseStarter', Icon: Salad },
  MAIN: { labelKey: 'courseMain', Icon: Utensils },
  DESSERT: { labelKey: 'courseDessert', Icon: Cookie },
};

/** Course name with a lucide icon (replaces emoji prefixes). */
function CourseLabel({ course, fallback }: { course: string | null; fallback?: string }) {
  const { t } = useTranslation('restaurant');
  const meta = course ? COURSE_META[course] : undefined;
  if (!meta) return <span>{fallback ?? course ?? ''}</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <meta.Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {t(meta.labelKey)}
    </span>
  );
}

// ── Local shapes for API payloads not covered by `@/types/restaurant` ─────────
/** Product row returned by `GET /products/pos` (only the fields this page reads). */
interface OrderProduct {
  id: string;
  name: string;
  salePrice?: number | string | null;
  sellingPrice?: number | string | null;
}

/** Category row returned by `GET /categories`. */
interface CategoryOption {
  id: string;
  name: string;
}

/** Customer shown in the bill dialog — a search result or the order's attached customer. */
interface BillCustomer {
  id?: string;
  name: string;
  phone?: string | null;
  loyaltyPoints?: number;
}

/** Row returned by `GET /customers/search` (always carries an id). */
interface SearchCustomer extends BillCustomer {
  id: string;
}

/** Dining order detail — the API also returns `totalAmount`, which the shared type omits. */
type DiningOrderDetail = DiningOrder & { totalAmount?: number | string | null };

/** Body for `POST /dining-orders/:id/bill`. */
interface BillPayload {
  paymentMethod: string;
  paidAmount: number;
  /** Receiving bank account for card / wallet / bank-transfer bills (T-488). */
  bankAccountId?: string;
  customerId?: string;
  loyaltyPointsToRedeem?: number;
  tipAmount?: number;
}

/** Line item in the split-bill preview. */
interface SplitBillLine {
  qty: number;
  name: string;
  lineTotal: number;
  /** POS-I120: line discount already taken off `lineTotal`. */
  discountAmount?: number;
}

/** Response of `GET /dining-orders/:id/split-bill`. */
interface SplitBillPreview {
  /** Net of the line discounts. */
  subtotal: number;
  /** POS-I120: money given away on the lines. */
  lineDiscountTotal?: number;
  totalTax: number;
  grandTotal: number;
  perPerson: number;
  lineItems?: SplitBillLine[];
}

// ── Cart item type for the multi-add cart ─────────────────────────────────────
interface CartItem {
  product: OrderProduct;
  quantity: number;
  notes: string;
  course: string;
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const { t } = useTranslation('restaurant');
  const canWrite = hasPermission('restaurant:write') || hasPermission('restaurant:*');
  const canUseLoyalty = hasPermission('loyalty:*');
  const canUseShifts = hasPermission('shifts:*') || hasPermission('shifts:manage');

  const TYPE_LABELS: Record<string, string> = {
    DINE_IN:  t('dineIn'),
    TAKEAWAY: t('takeaway'),
    DELIVERY: t('delivery'),
  };

  const DELIVERY_STATUS_LABELS: Record<string, string> = {
    PENDING: t('deliveryStatusPending'),
    ASSIGNED: t('deliveryStatusAssigned'),
    IN_TRANSIT: t('deliveryStatusInTransit'),
    DELIVERED: t('deliveryStatusDelivered'),
    FAILED: t('deliveryStatusFailed'),
  };

  const DELIVERY_STATUS_COLORS: Record<string, string> = {
    PENDING: 'bg-warning/10 text-warning-text',
    ASSIGNED: 'bg-info/10 text-info-text',
    IN_TRANSIT: 'bg-primary/10 text-primary-text',
    DELIVERED: 'bg-success/10 text-success-text',
    FAILED: 'bg-destructive/10 text-destructive-text',
  };
  const PAYMENT_METHODS = [
    { method: 'CASH',          label: t('paymentCash'),         icon: <Banknote className="h-5 w-5" /> },
    { method: 'CARD',          label: t('paymentCard'),         icon: <CreditCard className="h-5 w-5" /> },
    { method: 'MOBILE_WALLET', label: t('paymentMobileWallet'), icon: <Smartphone className="h-5 w-5" /> },
    { method: 'BANK_TRANSFER', label: t('paymentBank'),         icon: <Building2 className="h-5 w-5" /> },
  ];

  // Dialog state
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [billOpen, setBillOpen] = useState(false);
  const [splitBillOpen, setSplitBillOpen] = useState(false);
  const [splitCount, setSplitCount] = useState(2);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);

  // ── Multi-product add dialog state ──────────────────────────────────────
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);  // pending items to add

  // ── Bill dialog state ───────────────────────────────────────────────────
  const [paymentMethod, setPaymentMethod] = useState<string>('CASH');
  const [billBankAccountId, setBillBankAccountId] = useState('');
  const [amountTendered, setAmountTendered] = useState('');
  const [billCustomerSearch, setBillCustomerSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<SearchCustomer | null>(null);
  const [loyaltyPointsToRedeem, setLoyaltyPointsToRedeem] = useState('');
  const [tipAmount, setTipAmount] = useState(0);
  const [tipMode, setTipMode] = useState<'none' | 'pct' | 'custom'>('none');

  // ── Reassign server dialog state ────────────────────────────────────────
  const [reassignOpen, setReassignOpen] = useState(false);
  const [newServerId, setNewServerId] = useState('');

  // ── Transfer table dialog state ─────────────────────────────────────────
  const [transferOpen, setTransferOpen] = useState(false);
  const [newTableId, setNewTableId] = useState('');

  // ── Comp item dialog state ──────────────────────────────────────────────
  const [compItemId, setCompItemId] = useState<string | null>(null);
  const [compReason, setCompReason] = useState('');

  // ── Line (bargaining) discount, POS-I120 ───────────────────────────────
  const [editingDiscountId, setEditingDiscountId] = useState<string | null>(null);
  // The manager PIN switch and threshold the till already honours also gate big dining line discounts.
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenant-settings-checkout'],
    queryFn: () =>
      api
        .get('/tenant/settings')
        .then((r) => r.data as { managerPinEnabled?: boolean; managerDiscountThreshold?: number }),
    staleTime: 60_000,
  });
  const managerPinEnabled = tenantSettings?.managerPinEnabled ?? false;
  const managerDiscountThreshold = tenantSettings?.managerDiscountThreshold ?? 20;

  // ── Merge tables dialog state ───────────────────────────────────────────
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTableIds, setMergeTableIds] = useState<string[]>([]);

  const { data: order, isLoading } = useQuery<DiningOrderDetail>({
    queryKey: ['dining-order', id],
    queryFn: () => api.get(`/dining-orders/${id}`).then((r) => r.data),
    refetchInterval: 300000, // 5 min safety-net; real-time via WebSocket
  });

  // Real-time: refresh this order's detail when kitchen updates it
  const handleOrderUpdate = useCallback((payload: Record<string, unknown>) => {
    if (payload?.orderId === id || !payload?.orderId) {
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
    }
  }, [id, queryClient]);
  useSocketEvent('dining_order.updated', handleOrderUpdate);
  useSocketEvent('delivery.updated', handleOrderUpdate);
  useSocketEvent('kitchen.item_updated', handleOrderUpdate);

  // Staff users for waiter reassignment
  const { data: staffUsers } = useQuery<{ id: string; firstName: string; lastName: string }[]>({
    queryKey: ['staff-users'],
    queryFn: () => api.get('/users?limit=200').then((r) => {
      const list = r.data?.data ?? r.data ?? [];
      return list.map((u: { id: string; firstName: string; lastName: string }) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName }));
    }),
    enabled: reassignOpen,
  });

  // Available tables for transfer
  const { data: availableTables } = useQuery<{ id: string; tableNo: string; capacity: number }[]>({
    queryKey: ['available-tables-transfer'],
    queryFn: () => api.get('/restaurant/tables?status=AVAILABLE').then((r) => r.data?.data ?? r.data ?? []),
    enabled: transferOpen,
  });

  // Product search for the add-items panel
  const { data: allProducts } = useQuery<OrderProduct[]>({
    queryKey: ['products-for-order', productSearch, categoryFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (productSearch.length >= 1) params.set('search', productSearch);
      if (categoryFilter) params.set('categoryId', categoryFilter);
      const qs = params.toString();
      return api.get(`/products/pos${qs ? `?${qs}` : ''}`).then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : (d?.data ?? []);
      });
    },
    enabled: addItemOpen,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });

  // Categories for the sidebar filter
  const { data: categories } = useQuery<CategoryOption[] | { data: CategoryOption[] }>({
    queryKey: ['categories-simple'],
    queryFn: () => api.get('/categories').then((r) => r.data),
    enabled: addItemOpen,
    staleTime: 60_000,
  });

  // Bill dialog: customer search
  const { data: customerSearchResults } = useQuery({
    queryKey: ['customer-search-bill', billCustomerSearch],
    queryFn: () => api.get(`/customers/search?q=${encodeURIComponent(billCustomerSearch)}`).then((r) => r.data),
    enabled: billOpen && billCustomerSearch.length >= 2,
    staleTime: 30_000,
  });

  // Bill dialog: loyalty settings (only fetched when loyalty addon is active)
  const { data: loyaltySettings } = useQuery({
    queryKey: ['loyalty-settings'],
    queryFn: () => api.get('/loyalty/settings').then((r) => r.data),
    enabled: billOpen && canUseLoyalty,
    staleTime: 300_000,
  });

  // Shift check — only fetched when tenant has shifts addon
  const { data: currentShift } = useQuery({
    queryKey: ['current-shift'],
    queryFn: () => api.get('/shifts/current').then((r) => r.data),
    staleTime: 30_000,
    enabled: canUseShifts,
  });

  // Split bill preview
  const { data: splitBillData, isFetching: splitBillLoading } = useQuery<SplitBillPreview>({
    queryKey: ['split-bill-preview', id, splitCount],
    queryFn: () => api.get(`/dining-orders/${id}/split-bill?splits=${splitCount}`).then((r) => r.data),
    enabled: splitBillOpen && !!id,
    staleTime: 5_000,
  });

  // ── Cart helpers ────────────────────────────────────────────────────────
  const addToCart = (product: OrderProduct) => {
    setCartItems((prev) => {
      const existing = prev.find((c) => c.product.id === product.id);
      if (existing) return prev.map((c) => c.product.id === product.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { product, quantity: 1, notes: '', course: '' }];
    });
  };

  const updateCartQty = (productId: string, delta: number) => {
    setCartItems((prev) =>
      prev.flatMap((c) => {
        if (c.product.id !== productId) return [c];
        const q = c.quantity + delta;
        return q <= 0 ? [] : [{ ...c, quantity: q }];
      })
    );
  };

  const removeFromCart = (productId: string) => {
    setCartItems((prev) => prev.filter((c) => c.product.id !== productId));
  };

  const cartTotal = useMemo(
    () => cartItems.reduce((sum, c) => sum + Number(c.product.salePrice ?? 0) * c.quantity, 0),
    [cartItems]
  );

  const addItemsMutation = useMutation({
    mutationFn: (items: CartItem[]) =>
      api.post(`/dining-orders/${id}/items/bulk`, {
        items: items.map((c) => ({
          productId: c.product.id,
          quantity: c.quantity,
          notes: c.notes || undefined,
          course: c.course || undefined,
        })),
      }),
    onSuccess: () => {
      toast.success(`${cartItems.length} ${t('itemsLabel')} ${t('addedToOrder')}`);
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      setAddItemOpen(false);
      setCartItems([]);
      setProductSearch('');
      setCategoryFilter('');
    },
    onError: (err) => notify.error(err, t('failedToAddItems')),
  });

  const removeItemMutation = useMutation({
    mutationFn: (itemId: string) => api.delete(`/dining-orders/${id}/items/${itemId}`),
    onSuccess: () => {
      toast.success(t('itemRemoved'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      setDeleteItemId(null);
    },
    onError: (err) => notify.error(err, t('failedToRemoveItem')),
  });

  const sendToKitchenMutation = useMutation({
    mutationFn: () => api.post(`/dining-orders/${id}/send-to-kitchen`),
    onSuccess: () => {
      toast.success(t('sentToKitchen'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
    },
    onError: (err) => notify.error(err, t('failedToSendToKitchen')),
  });

  // Bank accounts for the bill dialog (T-488) - cashier-safe options endpoint, shared with POS checkout.
  const { data: billBankAccounts = [] } = useQuery<BankAccountOption[]>({
    queryKey: ['bank-accounts-options'],
    queryFn: () => api.get('/bank-accounts/options').then((r) => r.data as BankAccountOption[]),
    staleTime: 60_000,
    enabled: billOpen,
  });

  const billMutation = useMutation({
    mutationFn: (data: BillPayload) => api.post(`/dining-orders/${id}/bill`, data),
    onSuccess: (_res, variables) => {
      if (variables.bankAccountId) rememberBankAccountId(variables.bankAccountId);
      toast.success(t('orderBilledSuccess'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['products-pos'] });
      setBillOpen(false);
    },
    onError: (err) => notify.error(err, t('failedToBillOrder')),
  });

  const voidMutation = useMutation({
    mutationFn: () => api.post(`/dining-orders/${id}/void`),
    onSuccess: () => {
      toast.success(t('orderVoided'));
      queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      navigate('/restaurant/orders');
    },
    onError: () => toast.error(t('failedToVoidOrder')),
  });

  const reassignServerMutation = useMutation({
    mutationFn: (serverId: string) => api.patch(`/dining-orders/${id}/assign-server`, { serverId }),
    onSuccess: () => {
      toast.success(t('serverReassigned'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      setReassignOpen(false);
      setNewServerId('');
    },
    onError: (err) => notify.error(err, t('failedToReassignServer')),
  });

  const transferTableMutation = useMutation({
    mutationFn: (newTableId: string) => api.post(`/dining-orders/${id}/transfer-table`, { newTableId }),
    onSuccess: () => {
      toast.success(t('tableTransferred'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      queryClient.invalidateQueries({ queryKey: ['dining-orders'] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      queryClient.invalidateQueries({ queryKey: ['available-tables-transfer'] });
      setTransferOpen(false);
      setNewTableId('');
    },
    onError: (err) => notify.error(err, t('failedToTransferTable')),
  });

  const compItemMutation = useMutation({
    mutationFn: ({ itemId, reason }: { itemId: string; reason: string }) =>
      api.patch(`/dining-orders/${id}/items/${itemId}/comp`, { reason }),
    onSuccess: () => {
      toast.success(t('itemComped'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      setCompItemId(null);
      setCompReason('');
    },
    onError: (err) => notify.error(err, t('failedToCompItem')),
  });

  const setDiscountMutation = useMutation({
    mutationFn: ({ itemId, next }: { itemId: string; next: LineDiscountValue }) =>
      api.patch(`/dining-orders/${id}/items/${itemId}/discount`, {
        discountType: next.type,
        discountValue: Math.round(next.value * 100) / 100,
        reason: next.reason,
        approvedById: next.approvedById,
      }),
    onSuccess: () => {
      toast.success(t('diningDiscountSaved'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      queryClient.invalidateQueries({ queryKey: ['split-bill-preview', id] });
      setEditingDiscountId(null);
    },
    onError: (err) => notify.error(err, t('diningFailedToSetDiscount')),
  });

  const removeDiscountMutation = useMutation({
    mutationFn: (itemId: string) => api.delete(`/dining-orders/${id}/items/${itemId}/discount`),
    onSuccess: () => {
      toast.success(t('diningDiscountRemoved'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      queryClient.invalidateQueries({ queryKey: ['split-bill-preview', id] });
      setEditingDiscountId(null);
    },
    onError: (err) => notify.error(err, t('diningFailedToRemoveDiscount')),
  });

  // ── Tables available for merge ──────────────────────────────────────────
  const { data: mergeCandidates } = useQuery<Array<{ id: string; tableNo: string; capacity: number }>>({
    queryKey: ['available-tables-merge', id],
    queryFn: () => api.get('/restaurant/tables', { params: { status: 'AVAILABLE' } }).then(r =>
      (r.data?.data ?? r.data ?? []).filter((t: { id: string }) => t.id !== order?.tableId),
    ),
    enabled: mergeOpen && !!order?.tableId,
  });

  const mergeTablesMutation = useMutation({
    mutationFn: (tableIds: string[]) => api.post(`/dining-orders/${id}/merge-tables`, { tableIds }),
    onSuccess: () => {
      toast.success(t('tablesMerged'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
      setMergeOpen(false);
      setMergeTableIds([]);
    },
    onError: (err) => notify.error(err, t('failedToMergeTables')),
  });

  const unmergeTablesMutation = useMutation({
    mutationFn: (tableIds: string[]) => api.post(`/dining-orders/${id}/unmerge-tables`, { tableIds }),
    onSuccess: () => {
      toast.success(t('tablesUnmerged'));
      queryClient.invalidateQueries({ queryKey: ['dining-order', id] });
      queryClient.invalidateQueries({ queryKey: ['floor-plan'] });
    },
    onError: (err) => notify.error(err, t('failedToUnmergeTables')),
  });

  const handleBill = () => {
    // Enforce shift requirement only when the tenant has the Shifts addon
    if (canUseShifts && (!currentShift || currentShift.status !== 'OPEN')) {
      toast.error(t('openShiftRequired'));
      return;
    }
    const tendered = Number(amountTendered);
    const redeemPoints = Number(loyaltyPointsToRedeem || 0);
    const redeemRate = loyaltySettings?.isEnabled ? Number(loyaltySettings.redeemRate ?? 0.01) : 0;
    const loyaltyDiscount = redeemPoints * redeemRate;
    const effectiveTotal = Math.max(0, orderTotal - loyaltyDiscount) + tipAmount;

    if (!amountTendered || tendered <= 0) {
      toast.error(t('enterValidPaymentAmount'));
      return;
    }
    if (tendered < effectiveTotal) {
      toast.error(t('amountMustBeAtLeast', { amount: formatCurrency(effectiveTotal) }));
      return;
    }
    const effectiveCustId = selectedCustomerId ?? order?.customerId ?? null;
    billMutation.mutate({
      paymentMethod,
      paidAmount: tendered,
      bankAccountId: effectiveBankAccountId(paymentMethod, billBankAccountId, billBankAccounts),
      customerId: effectiveCustId || undefined,
      loyaltyPointsToRedeem: redeemPoints > 0 ? redeemPoints : undefined,
      tipAmount: tipAmount > 0 ? tipAmount : undefined,
    });
  };

  // POS-I120: the order value as the server bills it: unit price x quantity per active line, net of the
  // line (bargaining) discounts, comped / cancelled lines worth nothing (one owner: lib/dining-order-total).
  const orderTotal = order?.totalAmount != null
    ? Number(order.totalAmount)
    : diningItemsNet(order?.items ?? []);
  const itemDiscountTotal = diningItemsDiscount(order?.items ?? []);

  if (isLoading) {
    return (
      <div className="space-y-4 page-enter">
        <div className="h-8 w-48 rounded-lg bg-muted animate-pulse" />
        <div className="h-40 rounded-xl bg-muted animate-pulse" />
        <div className="h-64 rounded-xl bg-muted animate-pulse" />
      </div>
    );
  }

  if (!order) return <p className="text-muted-foreground">{t('orderNotFound')}</p>;

  const isEditable = ['OPEN', 'PREPARING', 'READY', 'SERVED'].includes(order.status);
  const isBillable = ['OPEN', 'PREPARING', 'READY', 'SERVED'].includes(order.status);
  const isVoidable = ['OPEN', 'PREPARING'].includes(order.status);

  return (
    <div className="space-y-6 pb-24 lg:pb-6 page-enter">
      <PageHeader
        title={t('orderNo', { no: order.orderNo })}
        subtitle={[
          order.table ? t('tableNo', { no: order.table.tableNo }) : '',
          order.type === 'TAKEAWAY' && order.carSlot ? order.carSlot : '',
          order.deliveryAddress ? order.deliveryAddress : '',
          order.customerName ?? '',
          order.customerPhone ?? '',
          t('guestsCount', { count: order.guestCount }),
          TYPE_LABELS[order.type],
          order.server ? `${order.server.firstName} ${order.server.lastName}`.trim() : '',
        ].filter(Boolean).join(' · ')}
        actions={
          <div className="flex gap-2">
            {canWrite && isEditable && order.type === 'DINE_IN' && (
              <Button variant="outline" onClick={() => setTransferOpen(true)}>
                <ArrowRightLeft className="h-4 w-4 me-1" />
                {t('transferTable')}
              </Button>
            )}
            {canWrite && isEditable && order.type === 'DINE_IN' && order.tableId && (
              <Button variant="outline" onClick={() => { setMergeOpen(true); setMergeTableIds([]); }}>
                <Combine className="h-4 w-4 me-1" />
                {t('mergeTables')}
              </Button>
            )}
            {canWrite && isEditable && (
              <Button variant="outline" onClick={() => { setReassignOpen(true); setNewServerId(order.serverId ?? ''); }}>
                <UserCheck className="h-4 w-4 me-1" />
                {t('reassignServer')}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/restaurant/orders')}>
              <ArrowLeft className="h-4 w-4 me-2" />
              {t('back')}
            </Button>
          </div>
        }
      />

      {/* Status + total row */}
      <div className="flex items-center gap-3">
        <span className={`rounded-full px-3 py-1 text-sm font-medium ${STATUS_COLORS[order.status] ?? ''}`}>
          {order.status}
        </span>
        {order.mergedTableIds && (order.mergedTableIds as string[]).length > 0 && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Combine className="h-4 w-4" />
            <span>{t('mergedWith', { count: (order.mergedTableIds as string[]).length })}</span>
            {canWrite && isEditable && (
              <Button
                variant="ghost"
                size="sm"
                className="h-11 px-2.5 text-sm text-destructive-text"
                onClick={() => unmergeTablesMutation.mutate(order.mergedTableIds as string[])}
              >
                {t('unmergeAll')}
              </Button>
            )}
          </div>
        )}
        <div className="ms-auto text-end">
          <p className="text-2xl font-bold">{formatCurrency(orderTotal)}</p>
          {itemDiscountTotal > 0 && (
            <p className="text-sm text-success-text">
              {t('diningItemDiscounts')}: −{formatCurrency(itemDiscountTotal)}
            </p>
          )}
        </div>
      </div>

      {order.type === 'DELIVERY' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className="h-4 w-4 text-muted-foreground" />
              {t('deliveryDispatch')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {order.deliveryAddress && (
              <p className="text-muted-foreground">{order.deliveryAddress}</p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-muted-foreground">{t('deliveryDispatchStatus')}</span>
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  DELIVERY_STATUS_COLORS[order.deliveryStatus ?? 'PENDING'] ?? 'bg-muted text-muted-foreground'
                }`}
              >
                {DELIVERY_STATUS_LABELS[order.deliveryStatus ?? 'PENDING'] ?? order.deliveryStatus ?? 'PENDING'}
              </span>
            </div>
            {(order.driver?.name ?? order.riderName) && (
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('deliveryRider')}</span>
                  <span className="font-medium text-foreground">{order.driver?.name ?? order.riderName}</span>
                </div>
                {(order.driver?.phone ?? order.riderPhone) && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{t('deliveryRiderPhone')}</span>
                    <span className="font-medium text-foreground">{order.driver?.phone ?? order.riderPhone}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t('orderItems')}</CardTitle>
            {canWrite && isEditable && (
              <Button className="h-11" onClick={() => setAddItemOpen(true)}>
                <Plus className="me-1.5 h-4 w-4" />
                {t('addItem')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {order.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t('noItemsYet')}</p>
          ) : (
            <div>
              {(['STARTER', 'MAIN', 'DESSERT', null] as const).map((course) => {
                const courseItems = order.items.filter((item) => (item.course ?? null) === course);
                if (courseItems.length === 0) return null;
                return (
                  <div key={course ?? 'uncategorised'} className="mb-2">
                    {course && (
                      <div className="px-1 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border/60 mb-1">
                        <CourseLabel course={course} />
                      </div>
                    )}
                    <div className="divide-y">
                      {courseItems.map((item) => {
                        const lineGross = diningLineGross(item);
                        const lineOff = diningLineDiscount(item);
                        const canDiscount = canWrite && isEditable && !item.isComped && item.kitchenStatus !== 'CANCELLED';
                        return (
                          <div key={item.id} className="flex items-start gap-3 py-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${item.isComped ? 'line-through text-muted-foreground' : ''}`}>{item.product?.name ?? item.productId}</span>
                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${KITCHEN_STATUS_COLORS[item.kitchenStatus] ?? ''}`}>
                                  {item.kitchenStatus}
                                </span>
                                {item.isComped && (
                                  <span className="rounded-full bg-primary/10 text-primary-text px-2 py-0.5 text-xs font-medium">
                                    COMP
                                  </span>
                                )}
                              </div>
                              {item.modifiers.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {item.modifiers.map((m, i) => (
                                    <span key={i} className="rounded bg-muted px-1.5 py-0.5 text-sm">
                                      {m.modifierName}: {m.optionName}
                                      {m.priceAdjust ? ` (+${formatCurrency(m.priceAdjust)})` : ''}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {item.notes && (
                                <p className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                                  <StickyNote className="h-4 w-4 shrink-0" aria-hidden="true" />
                                  {item.notes}
                                </p>
                              )}
                              {lineOff > 0 && (
                                <p className="flex flex-wrap items-center gap-1.5 text-sm text-success-text mt-0.5">
                                  <Tag className="h-4 w-4 shrink-0" aria-hidden="true" />
                                  <span>{t('diningDiscountOff', { amount: formatCurrency(lineOff) })}</span>
                                  {item.discountType === 'PERCENTAGE' && <span>({Number(item.discountValue)}%)</span>}
                                  {item.discountReason && <span>· {t('diningDiscountReason', { reason: item.discountReason })}</span>}
                                  {item.discountApprovedById && <span>· {t('diningDiscountApproved')}</span>}
                                </p>
                              )}
                              {editingDiscountId === item.id && canDiscount && (
                                <LineDiscountEditor
                                  itemName={item.product?.name ?? ''}
                                  grossAmount={lineGross}
                                  current={
                                    item.discountType
                                      ? {
                                          type: item.discountType,
                                          value: Number(item.discountValue ?? 0),
                                          reason: item.discountReason ?? undefined,
                                          approvedById: item.discountApprovedById ?? undefined,
                                        }
                                      : undefined
                                  }
                                  managerPinEnabled={managerPinEnabled}
                                  managerDiscountThreshold={managerDiscountThreshold}
                                  onApply={(next) => setDiscountMutation.mutate({ itemId: item.id, next })}
                                  onRemove={() => removeDiscountMutation.mutate(item.id)}
                                  onCancel={() => setEditingDiscountId(null)}
                                />
                              )}
                              {item.isComped && item.compReason && (
                                <p className="flex items-center gap-1.5 text-sm text-primary-text mt-0.5">
                                  <Gift className="h-4 w-4 shrink-0" aria-hidden="true" />
                                  {item.compReason}
                                </p>
                              )}
                            </div>
                            <div className="text-end flex-shrink-0">
                              {item.isComped ? (
                                <>
                                  <p className="font-medium line-through text-muted-foreground">{item.quantity} × {formatCurrency(Number(item.unitPrice))}</p>
                                  <p className="text-sm text-primary-text">{formatCurrency(0)}</p>
                                </>
                              ) : (
                                <>
                                  <p className="font-medium">{item.quantity} × {formatCurrency(Number(item.unitPrice))}</p>
                                  {lineOff > 0 ? (
                                    <>
                                      <p className="text-sm text-muted-foreground line-through">{formatCurrency(lineGross)}</p>
                                      <p className="text-sm font-medium">{formatCurrency(lineGross - lineOff)}</p>
                                    </>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">{formatCurrency(lineGross)}</p>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              {canDiscount && (
                                <Button
                                  variant="outline"
                                  className={`h-11 w-11 p-0 ${lineOff > 0 ? 'border-success/40 text-success-text' : 'text-primary-text'}`}
                                  onClick={() => setEditingDiscountId(editingDiscountId === item.id ? null : item.id)}
                                  title={lineOff > 0 ? t('diningDiscountEdit') : t('diningDiscountAdd')}
                                  aria-label={t('diningDiscountAria', { item: item.product?.name ?? '' })}
                                  aria-expanded={editingDiscountId === item.id}
                                >
                                  <Tag className="h-4 w-4" />
                                </Button>
                              )}
                              {canDiscount && (
                                <Button
                                  variant="outline"
                                  className="h-11 w-11 p-0 text-primary-text"
                                  onClick={() => { setCompItemId(item.id); setCompReason(''); }}
                                  title={t('compItem')}
                                  aria-label={t('compItem')}
                                >
                                  <Gift className="h-4 w-4" />
                                </Button>
                              )}
                              {canWrite && isEditable && item.kitchenStatus === 'PENDING' && (
                                <Button
                                  variant="outline"
                                  className="h-11 w-11 p-0 text-destructive-text"
                                  onClick={() => setDeleteItemId(item.id)}
                                  aria-label={t('deleteItem')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action buttons */}
      {order.status !== 'BILLED' && order.status !== 'COMPLETED' && order.status !== 'VOIDED' && (
        <div className="flex gap-3 flex-col sm:flex-row">
          {canWrite && order.status === 'OPEN' && order.items.length > 0 && (
            <Button
              className="h-11"
              onClick={() => sendToKitchenMutation.mutate()}
              disabled={sendToKitchenMutation.isPending}
            >
              <Send className="me-2 h-4 w-4" />
              {sendToKitchenMutation.isPending ? t('sending') : t('sendToKitchen')}
            </Button>
          )}
          {canWrite && isBillable && (
            <>
              <Button
                variant="outline"
                className="h-11"
                onClick={() => setSplitBillOpen(true)}
              >
                <Scissors className="me-2 h-4 w-4" />
                Split Bill
              </Button>
              <Button
                className="h-11"
                onClick={() => {
                  setPaymentMethod('CASH');
                  setAmountTendered(orderTotal.toFixed(2));
                  setTipAmount(0);
                  setTipMode('none');
                  setBillOpen(true);
                }}
              >
                <Receipt className="me-2 h-4 w-4" />
                {t('billOrder')}
              </Button>
            </>
          )}
          {canWrite && isVoidable && (
            <Button
              variant="destructive"
              className="h-11"
              onClick={() => setVoidConfirmOpen(true)}
            >
              <XCircle className="me-2 h-4 w-4" />
              {t('voidOrder')}
            </Button>
          )}
        </div>
      )}

      {order.status === 'BILLED' && order.saleId && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-2">
              <Receipt className="h-4 w-4 shrink-0" />
              <span>{t('orderBilledMessage')}</span>
              {order.sale?.invoiceNumber ? (
                <RecordLink type="invoice" id={order.sale.id ?? order.saleId} label={order.sale.invoiceNumber} variant="mono" className="text-xs font-semibold" />
              ) : (
                <Link
                  to="/sell/sales"
                  className="text-xs font-semibold text-primary-text underline-offset-2 hover:underline"
                >
                  {t('viewInvoice')}
                </Link>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* ───────────────────────────────────────────────────────────────────
          Multi-Product Add Dialog (POS-style)
          ─────────────────────────────────────────────────────────────── */}
      <Dialog
        visible={addItemOpen}
        onHide={() => { setAddItemOpen(false); setCartItems([]); setProductSearch(''); setCategoryFilter(''); }}
        header={
          <div className="flex items-center justify-between w-full pe-2">
            <span>{t('addItem')}</span>
            {cartItems.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                {cartItems.reduce((s, c) => s + c.quantity, 0)} item(s) — {formatCurrency(cartTotal)}
              </span>
            )}
          </div>
        }
        style={{ width: '100%', maxWidth: '56rem' }}
        contentStyle={{ padding: 0, height: '75vh', overflow: 'hidden' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3 p-3 border-t">
            <Button
              variant="outline"
              className="flex-none"
              onClick={() => { setAddItemOpen(false); setCartItems([]); setProductSearch(''); setCategoryFilter(''); }}
            >
              {t('cancel')}
            </Button>
            <Button
              className="flex-1"
              disabled={cartItems.length === 0 || addItemsMutation.isPending}
              onClick={() => addItemsMutation.mutate(cartItems)}
            >
              {addItemsMutation.isPending
                ? t('adding')
                : cartItems.length === 0
                  ? t('addItem')
                  : `${t('addItem')} ${cartItems.reduce((s, c) => s + c.quantity, 0)} — ${formatCurrency(cartTotal)}`
              }
            </Button>
          </div>
        }
      >
        <div className="flex h-full overflow-hidden">
          {/* Left — category sidebar */}
          <div className="w-36 sm:w-44 flex-none border-e overflow-y-auto bg-muted/30 p-2 space-y-1">
            <button
              className={`w-full text-start px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!categoryFilter ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
              onClick={() => setCategoryFilter('')}
            >
              {t('allCategories')}
            </button>
            {(Array.isArray(categories) ? categories : categories?.data ?? []).map((cat: CategoryOption) => (
              <button
                key={cat.id}
                className={`w-full text-start px-3 py-2 rounded-lg text-sm transition-colors ${categoryFilter === cat.id ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                onClick={() => setCategoryFilter(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Middle — product grid + search */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search bar */}
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="ps-9"
                  placeholder={t('searchProducts')}
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
              </div>
            </div>
            {/* Products grid */}
            <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 content-start">
              {(Array.isArray(allProducts) ? allProducts : []).map((p: OrderProduct) => {
                const inCart = cartItems.find((c) => c.product.id === p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => addToCart(p)}
                    className={`relative text-start rounded-xl border p-3 transition-all hover:shadow-md ${inCart ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
                  >
                    {inCart && (
                      <span className="absolute -top-2 -end-2 bg-primary text-primary-foreground rounded-full h-6 w-6 flex items-center justify-center text-xs font-bold">
                        {inCart.quantity}
                      </span>
                    )}
                    <div className="font-medium text-sm leading-tight mb-1 line-clamp-2">{p.name}</div>
                    <div className="text-sm text-primary-text font-semibold">{formatCurrency(Number(p.salePrice ?? p.sellingPrice ?? 0))}</div>
                  </button>
                );
              })}
              {(allProducts ?? []).length === 0 && (
                <div className="col-span-full text-center text-sm text-muted-foreground py-8">
                  {productSearch ? t('noProductsMatching', { search: productSearch }) : t('noProductsFound')}
                </div>
              )}
            </div>
          </div>

          {/* Right — cart panel */}
          {cartItems.length > 0 && (
            <div className="w-48 sm:w-56 flex-none border-s flex flex-col overflow-hidden">
              <div className="p-3 border-b font-semibold text-sm">
                <ShoppingCart className="inline h-4 w-4 me-1" />
                {t('toAdd', { count: cartItems.length })}
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {cartItems.map((c) => (
                  <div key={c.product.id} className="rounded-lg border bg-background p-2">
                    <div className="text-xs font-medium leading-tight mb-1 line-clamp-2">{c.product.name}</div>
                    <div className="flex items-center gap-1 mt-1">
                      <button
                        onClick={() => updateCartQty(c.product.id, -1)}
                        className="h-11 w-11 min-h-[44px] min-w-[44px] rounded-full border flex items-center justify-center text-base hover:bg-accent active:scale-95 transition"
                        aria-label="Decrease quantity"
                      >−</button>
                      <span className="flex-1 text-center text-base font-semibold">{c.quantity}</span>
                      <button
                        onClick={() => updateCartQty(c.product.id, 1)}
                        className="h-11 w-11 min-h-[44px] min-w-[44px] rounded-full border flex items-center justify-center text-base hover:bg-accent active:scale-95 transition"
                        aria-label="Increase quantity"
                      >+</button>
                    </div>
                    <div className="mt-1.5">
                      <Dropdown
                        value={c.course}
                        onChange={(e) => setCartItems((prev) => prev.map((ci) => ci.product.id === c.product.id ? { ...ci, course: e.value } : ci))}
                        options={[
                          { label: t('noCourse'), value: '' },
                          { label: t('courseStarter'), value: 'STARTER' },
                          { label: t('courseMain'), value: 'MAIN' },
                          { label: t('courseDessert'), value: 'DESSERT' },
                        ]}
                        optionLabel="label"
                        optionValue="value"
                        itemTemplate={(opt: { value: string }) => <CourseLabel course={opt.value || null} fallback={t('noCourse')} />}
                        valueTemplate={(opt: { value: string } | null, props) =>
                          opt ? <CourseLabel course={opt.value || null} fallback={t('noCourse')} /> : <span>{props?.placeholder}</span>}
                        className="w-full"
                        placeholder={t('noCourse')}
                      />
                    </div>
                    <div className="text-xs text-primary-text text-end mt-0.5">
                      {formatCurrency(Number(c.product.salePrice ?? c.product.sellingPrice ?? 0) * c.quantity)}
                    </div>
                    <button
                      onClick={() => removeFromCart(c.product.id)}
                      className="min-h-[44px] text-xs text-muted-foreground hover:text-destructive-text mt-1"
                    >
                      {t('remove')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* ───────────────────────────────────────────────────────────────────
          Split Bill Dialog
          ─────────────────────────────────────────────────────────────── */}
      <Dialog
        visible={splitBillOpen}
        onHide={() => setSplitBillOpen(false)}
        header="Split Bill"
        style={{ width: '100%', maxWidth: '28rem' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setSplitBillOpen(false)}>Close</Button>
            <Button
              className="flex-1"
              onClick={() => {
                setSplitBillOpen(false);
                setPaymentMethod('CASH');
                setAmountTendered(splitBillData?.perPerson?.toFixed(2) ?? orderTotal.toFixed(2));
                setBillOpen(true);
              }}
            >
              <Receipt className="me-2 h-4 w-4" />
              Bill Full Order
            </Button>
          </div>
        }
      >
        <div className="space-y-5 p-2">
          <div>
            <Label className="mb-1 block">Number of guests</Label>
            <div className="flex items-center gap-3">
              <button
                className="flex h-11 w-11 items-center justify-center rounded-lg border hover:bg-muted transition-colors text-lg font-bold"
                onClick={() => setSplitCount((n) => Math.max(2, n - 1))}
                disabled={splitCount <= 2}
              >−</button>
              <span className="text-2xl font-bold w-8 text-center">{splitCount}</span>
              <button
                className="flex h-11 w-11 items-center justify-center rounded-lg border hover:bg-muted transition-colors text-lg font-bold"
                onClick={() => setSplitCount((n) => Math.min(20, n + 1))}
              >+</button>
            </div>
          </div>

          {splitBillLoading ? (
            <div className="text-center py-4 text-muted-foreground text-sm">Calculating...</div>
          ) : splitBillData ? (
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatCurrency(splitBillData.subtotal)}</span>
                </div>
                {(splitBillData.lineDiscountTotal ?? 0) > 0 && (
                  <div className="flex justify-between text-sm text-success-text">
                    <span>{t('diningYouSaved')} ({t('diningItemDiscounts')})</span>
                    <span>{formatCurrency(splitBillData.lineDiscountTotal ?? 0)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>{formatCurrency(splitBillData.totalTax)}</span>
                </div>
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Grand Total</span>
                  <span>{formatCurrency(splitBillData.grandTotal)}</span>
                </div>
              </div>

              <div className="rounded-xl bg-primary/10 border border-primary/20 p-4 text-center">
                <p className="text-xs text-muted-foreground mb-1">Per person ({splitCount} guests)</p>
                <p className="text-3xl font-bold text-primary">{formatCurrency(splitBillData.perPerson)}</p>
              </div>

              <div className="space-y-1 max-h-40 overflow-y-auto">
                {splitBillData.lineItems?.map((item: SplitBillLine, i: number) => (
                  <div key={i} className="flex justify-between text-xs text-muted-foreground">
                    <span className="truncate me-2">{item.qty}× {item.name}</span>
                    <span className="shrink-0">{formatCurrency(item.lineTotal / splitCount)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </Dialog>

      {/* ───────────────────────────────────────────────────────────────────
          POS-Style Bill Dialog
          ─────────────────────────────────────────────────────────────── */}
      <Dialog
        visible={billOpen}
        onHide={() => { setBillOpen(false); setSelectedCustomerId(null); setSelectedCustomer(null); setBillCustomerSearch(''); setLoyaltyPointsToRedeem(''); }}
        header={t('completePayment')}
        style={{ width: '100%', maxWidth: '36rem' }}
        contentStyle={{ maxHeight: '90vh', overflowY: 'auto' }}
        draggable={false}
        resizable={false}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setBillOpen(false)}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              onClick={handleBill}
              disabled={billMutation.isPending}
            >
              {billMutation.isPending ? t('processing') : t('completeBill')}
            </Button>
          </div>
        }
      >
        {(() => {
          const redeemPoints = Number(loyaltyPointsToRedeem || 0);
          const redeemRate = loyaltySettings?.isEnabled ? Number(loyaltySettings.redeemRate ?? 0.01) : 0;
          const loyaltyDiscount = redeemPoints * redeemRate;
          const subtotalAfterDiscount = Math.max(0, orderTotal - loyaltyDiscount);
          const effectiveTotal = subtotalAfterDiscount + tipAmount;
          const effectiveChange = Math.max(0, Number(amountTendered || 0) - effectiveTotal);

          // Resolved customer: override > order customer
          const resolvedCustomer: BillCustomer | null = selectedCustomer ?? order.customer ?? null;
          const availablePoints = resolvedCustomer?.loyaltyPoints ?? 0;
          const redeemRatePKR = loyaltySettings?.redeemRate ? Number(loyaltySettings.redeemRate) : 0.01;
          const pointsValuePKR = availablePoints * redeemRatePKR;
          const earnRate = loyaltySettings?.earnRatePKR ? Number(loyaltySettings.earnRatePKR) : 10;
          const earnPts = loyaltySettings?.earnRatePoints ?? 1;
          const pointsToEarn = loyaltySettings?.isEnabled ? Math.floor(Number(orderTotal) / earnRate) * earnPts : 0;

          const billQuickAmounts = [
            effectiveTotal,
            Math.ceil(effectiveTotal / 100) * 100,
            Math.ceil(effectiveTotal / 500) * 500,
            Math.ceil(effectiveTotal / 1000) * 1000,
          ].filter((v, i, arr) => arr.indexOf(v) === i && v >= effectiveTotal);

          return (
            <div className="space-y-5 pt-1">
              {/* Order summary */}
              <div className="rounded-xl border bg-muted/30 p-3 space-y-1 max-h-40 overflow-y-auto">
                {order.items.filter(it => it.kitchenStatus !== 'CANCELLED').map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{item.quantity}× {item.product?.name ?? 'Item'}</span>
                    <span>
                      {diningLineDiscount(item) > 0 && (
                        <span className="me-2 text-xs text-success-text">
                          {t('diningDiscountOff', { amount: formatCurrency(diningLineDiscount(item)) })}
                        </span>
                      )}
                      {formatCurrency(diningLineGross(item) - diningLineDiscount(item))}
                    </span>
                  </div>
                ))}
              </div>

              {/* Customer selection */}
              <div>
                <Label className="mb-1.5 block">{t('customerOptional')}</Label>
                {resolvedCustomer ? (
                  <div className="flex items-center justify-between rounded-lg border bg-primary/5 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">{resolvedCustomer.name}</p>
                      {loyaltySettings?.isEnabled && (
                        <p className="text-xs text-muted-foreground">
                          {availablePoints.toLocaleString()} pts available ({formatCurrency(pointsValuePKR)})
                        </p>
                      )}
                    </div>
                    <button
                      className="text-xs text-muted-foreground hover:text-destructive-text"
                      onClick={() => { setSelectedCustomerId(null); setSelectedCustomer(null); setBillCustomerSearch(''); setLoyaltyPointsToRedeem(''); }}
                    >
                      {t('change')}
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      placeholder={t('searchCustomerPlaceholder')}
                      value={billCustomerSearch}
                      onChange={(e) => setBillCustomerSearch(e.target.value)}
                    />
                    {billCustomerSearch.length >= 2 && customerSearchResults?.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-lg max-h-48 overflow-y-auto">
                        {customerSearchResults.map((c: SearchCustomer) => (
                          <button
                            key={c.id}
                            className="w-full text-start px-3 py-2 text-sm hover:bg-accent"
                            onClick={() => {
                              setSelectedCustomerId(c.id);
                              setSelectedCustomer(c);
                              setBillCustomerSearch('');
                            }}
                          >
                            <span className="font-medium">{c.name}</span>
                            {c.phone && <span className="ms-2 text-muted-foreground text-xs">{c.phone}</span>}
                            {loyaltySettings?.isEnabled && (c.loyaltyPoints ?? 0) > 0 && (
                              <span className="ms-2 text-xs text-warning-text">{c.loyaltyPoints} pts</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {billCustomerSearch.length >= 2 && customerSearchResults?.length === 0 && (
                      <div className="absolute z-50 mt-1 w-full rounded-lg border bg-popover shadow-lg px-3 py-2 text-sm text-muted-foreground">
                        {t('noCustomersFound')}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Loyalty redemption */}
              {loyaltySettings?.isEnabled && resolvedCustomer && availablePoints >= (loyaltySettings.minRedeemPoints ?? 100) && (
                <div className="rounded-xl border border-warning/30 bg-warning/10 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-warning-text">{t('redeemLoyaltyPoints')}</Label>
                    <span className="text-xs text-warning-text">{availablePoints.toLocaleString()} available</span>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      max={availablePoints}
                      value={loyaltyPointsToRedeem}
                      onChange={(e) => setLoyaltyPointsToRedeem(e.target.value)}
                      placeholder={t('pointsToRedeem')}
                      className="text-sm"
                    />
                    <button
                      className="px-3 py-1 rounded-lg border border-warning/30 text-xs font-medium text-warning-text hover:bg-warning/10"
                      onClick={() => setLoyaltyPointsToRedeem(String(availablePoints))}
                    >
                      {t('max')}
                    </button>
                  </div>
                  {redeemPoints > 0 && (
                    <p className="text-xs text-warning-text">
                      −{formatCurrency(loyaltyDiscount)} off your order
                    </p>
                  )}
                </div>
              )}

              {/* Total (with loyalty discount + tip if applied) */}
              <div className="space-y-1">
                {(loyaltyDiscount > 0 || tipAmount > 0 || itemDiscountTotal > 0) && (
                  <div className="flex justify-between text-sm text-muted-foreground px-1">
                    <span>{t('subtotal')}</span>
                    <span>{formatCurrency(orderTotal)}</span>
                  </div>
                )}
                {itemDiscountTotal > 0 && (
                  <div className="flex justify-between text-sm text-success-text px-1">
                    <span>{t('diningYouSaved')} ({t('diningItemDiscounts')})</span>
                    <span>{formatCurrency(itemDiscountTotal)}</span>
                  </div>
                )}
                {loyaltyDiscount > 0 && (
                  <div className="flex justify-between text-sm text-warning-text px-1">
                    <span>{t('loyaltyDiscount')}</span>
                    <span>−{formatCurrency(loyaltyDiscount)}</span>
                  </div>
                )}
                {tipAmount > 0 && (
                  <div className="flex justify-between text-sm text-success-text px-1">
                    <span>{t('tip')}</span>
                    <span>+{formatCurrency(tipAmount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between rounded-xl border bg-primary/5 px-4 py-3">
                  <span className="text-sm font-medium">{t('amountToPay')}</span>
                  <span className="text-2xl font-bold text-primary">{formatCurrency(effectiveTotal)}</span>
                </div>
              </div>

              {/* Tip / Gratuity */}
              <div>
                <Label className="mb-2 block">{t('tipGratuity')}</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[5, 10, 15, 20].map((pct) => {
                    const pctAmount = Math.round(subtotalAfterDiscount * pct / 100 * 100) / 100;
                    const isActive = tipMode === 'pct' && Math.abs(tipAmount - pctAmount) < 0.01;
                    return (
                      <button
                        key={pct}
                        onClick={() => { setTipAmount(pctAmount); setTipMode('pct'); }}
                        className={`min-h-[44px] rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                          isActive
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'hover:bg-accent'
                        }`}
                      >
                        {pct}%
                      </button>
                    );
                  })}
                  {tipAmount > 0 && (
                    <button
                      onClick={() => { setTipAmount(0); setTipMode('none'); }}
                      className="min-h-[44px] rounded-lg border border-destructive/30 px-4 py-2.5 text-sm font-medium text-destructive-text hover:bg-destructive/10"
                    >
                      {t('clearTip')}
                    </button>
                  )}
                </div>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={t('customTipAmount')}
                  value={tipMode === 'custom' ? tipAmount || '' : ''}
                  onChange={(e) => {
                    const val = Math.max(0, Number(e.target.value));
                    setTipAmount(val);
                    setTipMode(val > 0 ? 'custom' : 'none');
                  }}
                  className="text-sm"
                />
                {tipAmount > 0 && (
                  <p className="text-xs text-success-text mt-1">
                    {t('tipAdded', { amount: formatCurrency(tipAmount) })}
                  </p>
                )}
              </div>

              {/* Points to earn */}
              {loyaltySettings?.isEnabled && resolvedCustomer && pointsToEarn > 0 && (
                <p className="text-xs text-center text-success-text">
                  {t('pointsToEarn', { count: pointsToEarn })}
                </p>
              )}

              {/* Payment method */}
              <div>
                <Label className="mb-2 block">{t('paymentMethod')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map(({ method, label, icon }) => (
                    <button
                      key={method}
                      onClick={() => { setPaymentMethod(method); setBillBankAccountId(''); }}
                      className={`flex items-center gap-2 rounded-xl border p-3 text-sm font-medium transition-all ${
                        paymentMethod === method
                          ? 'border-primary bg-primary/10 text-primary-text ring-1 ring-primary'
                          : 'hover:border-primary/40 hover:bg-accent'
                      }`}
                    >
                      {icon}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Receiving bank account for card / wallet / bank transfer (hidden when <= 1 account) */}
              <BankAccountField
                method={paymentMethod}
                accounts={billBankAccounts}
                value={billBankAccountId}
                onChange={setBillBankAccountId}
              />

              {/* Quick amounts */}
              <div>
                <Label className="mb-2 block">{t('quickAmount')}</Label>
                <div className="flex flex-wrap gap-2">
                  {billQuickAmounts.slice(0, 4).map((amt) => (
                    <button
                      key={amt}
                      onClick={() => setAmountTendered(amt.toFixed(2))}
                      className={`min-h-[44px] rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                        Number(amountTendered) === amt
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      }`}
                    >
                      {formatCurrency(amt)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount tendered */}
              <div>
                <Label className="mb-1.5 block">{t('amountTendered')}</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountTendered}
                  onChange={(e) => setAmountTendered(e.target.value)}
                  className="h-12 text-lg font-semibold"
                />
              </div>

              {/* Change */}
              {effectiveChange > 0 && (
                <div className="flex items-center justify-between rounded-xl border border-success/30 bg-success/10 px-4 py-3">
                  <span className="text-sm font-medium">{t('changeDue')}</span>
                  <span className="text-xl font-bold text-success-text">{formatCurrency(effectiveChange)}</span>
                </div>
              )}
            </div>
          );
        })()}
      </Dialog>

      {/* Void confirm */}
      <ConfirmDialog
        open={voidConfirmOpen}
        title={t('voidOrder')}
        message={t('voidOrderConfirm')}
        confirmLabel={t('voidOrder')}
        variant="danger"
        loading={voidMutation.isPending}
        onConfirm={() => voidMutation.mutate()}
        onCancel={() => setVoidConfirmOpen(false)}
      />

      {/* Delete item confirm */}
      <ConfirmDialog
        open={!!deleteItemId}
        title={t('removeItem')}
        message={t('removeItemConfirm')}
        confirmLabel={t('remove')}
        variant="danger"
        loading={removeItemMutation.isPending}
        onConfirm={() => deleteItemId && removeItemMutation.mutate(deleteItemId)}
        onCancel={() => setDeleteItemId(null)}
      />

      {/* Reassign Server Dialog */}
      <Dialog
        visible={reassignOpen}
        onHide={() => { setReassignOpen(false); setNewServerId(''); }}
        header={t('reassignServer')}
        className="w-full max-w-md"
        modal
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">{t('reassignServerHelp')}</p>
          <Combobox
            options={(staffUsers ?? []).map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}`.trim() }))}
            value={newServerId}
            onChange={(val) => setNewServerId(val ?? '')}
            placeholder={t('selectWaiterPlaceholder')}
            emptyText={t('noStaffFound')}
          />
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setReassignOpen(false)}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              disabled={!newServerId || reassignServerMutation.isPending}
              onClick={() => reassignServerMutation.mutate(newServerId)}
            >
              {reassignServerMutation.isPending ? t('processing') : t('assignServer')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Transfer Table Dialog */}
      <Dialog
        visible={transferOpen}
        onHide={() => { setTransferOpen(false); setNewTableId(''); }}
        header={t('transferTable')}
        className="w-full max-w-md"
        modal
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">{t('transferTableHelp')}</p>
          {order && order.table && (
            <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
              {t('currentTable')}: <span className="font-medium">{order.table.tableNo}</span>
            </div>
          )}
          <Combobox
            options={(availableTables ?? []).map((tbl) => ({ value: tbl.id, label: `Table #${tbl.tableNo} (${tbl.capacity} seats)` }))}
            value={newTableId}
            onChange={(val) => setNewTableId(val ?? '')}
            placeholder={t('selectNewTable')}
            emptyText={t('noAvailableTables')}
          />
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setTransferOpen(false)}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              disabled={!newTableId || transferTableMutation.isPending}
              onClick={() => transferTableMutation.mutate(newTableId)}
            >
              {transferTableMutation.isPending ? t('processing') : t('transfer')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Merge Tables Dialog */}
      <Dialog
        visible={mergeOpen}
        onHide={() => { setMergeOpen(false); setMergeTableIds([]); }}
        header={t('mergeTables')}
        className="w-full max-w-md"
        modal
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">{t('mergeTablesHelp')}</p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {(mergeCandidates ?? []).map((tbl) => {
              const checked = mergeTableIds.includes(tbl.id);
              return (
                <Label key={tbl.id} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${checked ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                  <Checkbox
                    checked={checked}
                    onChange={() => setMergeTableIds(prev => checked ? prev.filter(x => x !== tbl.id) : [...prev, tbl.id])}
                  />
                  <span className="font-medium">Table #{tbl.tableNo}</span>
                  <span className="text-sm text-muted-foreground ms-auto">{tbl.capacity} seats</span>
                </Label>
              );
            })}
            {(mergeCandidates ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">{t('noAvailableTables')}</p>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setMergeOpen(false)}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              disabled={mergeTableIds.length === 0 || mergeTablesMutation.isPending}
              onClick={() => mergeTablesMutation.mutate(mergeTableIds)}
            >
              {mergeTablesMutation.isPending ? t('processing') : t('mergeSelected', { count: mergeTableIds.length })}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Comp Item Dialog */}
      <Dialog
        visible={!!compItemId}
        onHide={() => { setCompItemId(null); setCompReason(''); }}
        header={t('compItem')}
        className="w-full max-w-md"
        modal
        draggable={false}
        resizable={false}
      >
        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">{t('compItemHelp')}</p>
          <div className="space-y-2">
            <Label>{t('compReason')}</Label>
            <div className="flex flex-wrap gap-2">
              {['Manager approved', 'Customer complaint', 'Kitchen error', 'Quality issue'].map((reason) => (
                <Button
                  key={reason}
                  variant={compReason === reason ? 'default' : 'outline'}
                  onClick={() => setCompReason(reason)}
                >
                  {reason}
                </Button>
              ))}
            </div>
            <Input
              value={compReason}
              onChange={(e) => setCompReason(e.target.value)}
              placeholder={t('customCompReason')}
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setCompItemId(null)}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              disabled={!compReason.trim() || compItemMutation.isPending}
              onClick={() => compItemId && compItemMutation.mutate({ itemId: compItemId, reason: compReason })}
            >
              {compItemMutation.isPending ? t('processing') : t('markAsComp')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
